<template>
  <div class="pageContainer">
    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleDeviceInfo"
          :aria-expanded="deviceInfoOpen"
      >
        <font-awesome-icon
            :icon="['fas', deviceInfoOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Device Info</h6>
    </BannerComponent>

    <div class="card cardBackground" :class="{ 'free-user-section': subscriptionType === 'free' }" v-if="deviceInfoOpen">
      <div class="cardLine">
        <p>Version:</p>
        <p>{{deviceData.version}}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Serial Number:</p>
        <p>{{deviceData.serialNumber}}</p>
      </div>
      
      <!-- Premium Feature Overlay for Free Users -->
      <div v-if="subscriptionType === 'free'" class="feature-overlay" @click="showUpgradePrompt('DEVICES_MANAGER')">
        <div class="overlay-content">
          <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
          <span class="overlay-text">Upgrade Required</span>
        </div>
      </div>
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleAppearance"
          :aria-expanded="appearanceOpen"
      >
        <font-awesome-icon
            :icon="['fas', appearanceOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Appearance</h6>
    </BannerComponent>

    <div class="card cardBackground" v-if="appearanceOpen">
      <div class="cardLine">
        <p>Theme:</p>
        <div class="theme-options">
          <button
            class="theme-button"
            :class="{ active: currentTheme === 'light' }"
            @click="setTheme('light')"
          >
            <font-awesome-icon :icon="['fas', 'sun']" />
            <span>Light</span>
          </button>
          <button
            class="theme-button"
            :class="{ active: currentTheme === 'dark' }"
            @click="setTheme('dark')"
          >
            <font-awesome-icon :icon="['fas', 'moon']" />
            <span>Dark</span>
          </button>
        </div>
      </div>
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleSubscriptions"
          :aria-expanded="subscriptionsOpen"
      >
        <font-awesome-icon
            :icon="['fas', subscriptionsOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Billing & Subscriptions</h6>
    </BannerComponent>
    <div class="card cardBackground" v-if="subscriptionsOpen">
      <div class="cardLine">
        <p>Subscription:</p>
        <p>{{ subscriptionType === 'free' ? 'Free Plan' : 'Device Plan' }}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Next Payment:</p>
        <p>{{ subscriptionData.nextPaymentDate ? subscriptionData.nextPaymentDate.format('MM/DD/YYYY') : 'N/A' }}</p>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Payment Method:</p>
        <p>{{ subscriptionData.paymentMethod }}</p>
      </div>
<!--      <div class="lineDiv"></div>
      <div class="cardLine">
        <p>Protection Plan:</p>
        <p>{{subscriptionData.protectionPlanType}}</p>
      </div>-->
    </div>

    <button class="primaryButton" v-if="subscriptionsOpen" @click="goToBilling" style="margin-top: 0;">Manage</button>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleAdvanced"
          :aria-expanded="advancedOpen"
      >
        <font-awesome-icon
            :icon="['fas', advancedOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Advanced Settings</h6>
    </BannerComponent>

    <div class="card cardBackground" :class="{ 'free-user-section': subscriptionType === 'free' }" v-if="advancedOpen">
      <div class="cardLine">
        <p>DHCP Server</p>
        <p>Coming Soon</p>
      </div>
      <div class="lineDiv"></div>
      
      <!-- Premium Feature Overlay for Free Users -->
      <div v-if="subscriptionType === 'free'" class="feature-overlay" @click="showUpgradePrompt('NETWORK')">
        <div class="overlay-content">
          <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
          <span class="overlay-text">Upgrade Required</span>
        </div>
      </div>
    </div>

<!--    <BannerComponent>
      <div class="resetContainer">
        <h6 class="banner-heading" style="font-size: 1.5rem;">Factory Reset</h6>
        <button class="primaryButton" @click="openModal('reset')">Reset</button>
      </div>
    </BannerComponent>-->

    <div class="accountManagement">
      <button class="primaryButton" @click="goToResetPassword">Reset Password</button>
      <button class="secondaryButton" @click="logout">Logout</button>
    </div>


    <BaseModal
        :visible="modal.open"
        :title="modal.title"
        :submit-color="modal.submitColor"
        :submit-border-color="modal.submitColorBorder"
        :cancel-color="modal.cancelColor"
        :cancel-border-color="modal.cancelColorBorder"
        :cancel-show="modal.cancelShow"
        :submit-label="modal.submitLabel"
        @close="closeModal"
        @cancel="closeModal"
        @submit="submitModal"
    >
      <div class="modalContainer" v-if="modal.mode === 'reset'">
        <div class="modalDiv">
          <h6>This will erase all your data, settings, and configurations and restore the system to its original state.</h6>
          <br>
          <h6>This action cannot be undone.</h6>
        </div>
      </div>
    </BaseModal>

    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import dayjs from "dayjs";
import BaseModal from "@/components/BaseModal.vue";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import PaymentsAPIService from "@/servicehandlers/PaymentsAPIService";
import SubscriptionPermissionService from "@/services/SubscriptionPermissionService";
import {useLoginStore} from "@/stores/loginStore";
import {useThemeStore} from "@/stores/themeStore";
import LoadingPage from "@/components/LoadingPage.vue";

const deviceAPIService = new DeviceAPIService()
const paymentsAPIService = new PaymentsAPIService()

export default {
  name: 'SettingsPage',
  components: {BaseModal, BannerComponent, MessageModal, LoadingPage},
  data(){
    return {
      loading: false,
      appMessage: {
        text: '',
        type: 'error',
      },
      deviceInfoOpen: true,
      deviceData: {
        version: '',
        serialNumber: '',
      },
      appearanceOpen: true,
      subscriptionsOpen: true,
      subscriptionData: {
        nextPaymentDate: null,
        paymentMethod: "",
        customerData: null
      },
      advancedOpen: true,
      modal: {
        open: false,
        mode: '',
        title: '',
        submitColor: '',
        submitColorBorder: '',
        submitLabel: '',
        cancelColor: '',
        cancelColorBorder: '',
        cancelShow: true,
      },
      themeStore: null,
    }
  },
  computed: {
    currentTheme() {
      return this.themeStore?.currentTheme || 'dark';
    },
    subscriptionType() {
      return useLoginStore().getSubscriptionType
    },
    isDeviceOnline() {
      return useLoginStore().getDeviceOnline
    },
    canAccessDeviceFeatures() {
      return SubscriptionPermissionService.canAccessFeature(
        this.subscriptionType,
        'NETWORK_STATS', // Using NETWORK_STATS as proxy for device features
        this.isDeviceOnline
      )
    },
  },
  mounted(){
    //console.clear()
    this.themeStore = useThemeStore();

    // Only attempt device operations for users with device subscription
    if (this.subscriptionType === 'device' && useLoginStore().getDeviceOnline) {
      this.getDeviceInfo()
    } else if (this.subscriptionType === 'device' && !useLoginStore().getDeviceOnline) {
      this.appMessage.text = "Device Offline"
      this.appMessage.type = 'info'
      this.$refs.messageModal.open()
    }

    // Get customer data from Stripe
    this.getCustomerData()
    this.loadBasicSubscriptionData()
  },
  methods: {
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    async getDeviceInfo() {
      // Only fetch device info for users with device subscription
      if (this.subscriptionType !== 'device') {
        return
      }
      
      try {
        this.loading = true;
        let serialNumber = await deviceAPIService.getSerialNumber(this.$router, useLoginStore())
        this.deviceData.serialNumber = serialNumber.machineId;

        let versionNumber = await deviceAPIService.getVersionNumber(this.$router, useLoginStore())
        this.deviceData.version = versionNumber.version;

        let releaseVersion = await deviceAPIService.getCurrentReleaseVersion(this.$router, useLoginStore())
        this.deviceData.releaseVersion = releaseVersion.release_version_number

        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.showError(e)
      }
    },
    async getCustomerData() {
      try {
        this.loading = true;
        this.subscriptionData.customerData = await paymentsAPIService.getCustomer(this.$router);
        console.log(this.subscriptionData.customerData)
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.showError(e);
      }
    },
    async loadBasicSubscriptionData() {
      try {
        // Only load subscription details for paying users
        if (this.subscriptionType !== 'free') {
          // Load subscription info for next payment date
          const subscriptions = await paymentsAPIService.getSubscriptions(this.$router);
          
          if (subscriptions && subscriptions.length > 0) {
            const subscription = subscriptions[0];
            // Set next payment date
            this.subscriptionData.nextPaymentDate = dayjs(subscription.current_period_end * 1000);
          } else {
            this.subscriptionData.nextPaymentDate = null;
          }
          
          // Load payment method info for paying users
          const paymentMethods = await paymentsAPIService.getPaymentMethods(this.$router);
          if (paymentMethods && paymentMethods.length > 0) {
            const defaultMethod = paymentMethods.find(pm => pm.is_default) || paymentMethods[0];
            this.subscriptionData.paymentMethod = `•••• •••• •••• ${defaultMethod.card.last4}`;
          } else {
            this.subscriptionData.paymentMethod = 'No payment method';
          }
        } else {
          // Free users
          this.subscriptionData.nextPaymentDate = null;
          this.subscriptionData.paymentMethod = 'No payment method';
        }
        
      } catch (e) {
        console.error('Failed to load basic subscription data:', e);
        // Set fallback values
        this.subscriptionData.nextPaymentDate = null;
        this.subscriptionData.paymentMethod = 'No payment method';
      }
    },
    toggleDeviceInfo(){
      this.deviceInfoOpen = !this.deviceInfoOpen
    },
    toggleAppearance(){
      this.appearanceOpen = !this.appearanceOpen
    },
    toggleSubscriptions(){
      this.subscriptionsOpen = !this.subscriptionsOpen
    },
    toggleAdvanced(){
      this.advancedOpen = !this.advancedOpen
    },
    setTheme(theme) {
      // Update theme in theme store
      this.themeStore.setTheme(theme);
      
      // Save theme preference in login store
      const loginStore = useLoginStore();
      loginStore.setSelectedTheme(theme);
    },
    goToBilling(){
      this.$router.push({
        name: 'billingSubscription'
      })
    },
    goToResetPassword(){
      this.$router.push('/forgot-password')
    },
    openModal(mode){
      this.modal.mode = mode
      if (mode === 'reset'){
        this.modal.title = 'Are You Sure?'
        this.modal.submitColor = 'var(--color-red-button)'
        this.modal.submitColorBorder = 'var(--color-red-button-border)'
        this.modal.cancelColor = 'var(--color-primary-button)'
        this.modal.cancelColorBorder = 'var(--color-primary-button-border)'
        this.modal.submitLabel = 'Reset'
        this.modal.open = true
      }
    },
    closeModal(){
      this.modal = {
        open: false,
        mode: '',
        title: '',
        submitColor: '',
        submitColorBorder: '',
        submitLabel: '',
        cancelColor: '',
        cancelColorBorder: '',
        cancelShow: true,
      }
    },
    submitModal(){
      console.log('submit')
    },
    logout(){
      console.log('logout')
      useLoginStore().logOut()
      this.$router.push({
        name: 'login'
      })
    },
    showUpgradePrompt(featureName) {
      // Access the Menus component through the parent chain and call showUpgradeModal
      let parent = this.$parent;
      while (parent && !parent.showUpgradeModal) {
        parent = parent.$parent;
      }
      if (parent && parent.showUpgradeModal) {
        // Create a dummy route name based on the feature
        const routeMap = {
          'DEVICES_MANAGER': 'devicesManager',
          'NETWORK': 'network'
        };
        const routeName = routeMap[featureName] || 'settings';
        parent.showUpgradeModal(routeName);
      }
    },
  },
}
</script>

<style scoped>
.accountManagement{
  display: flex;
  justify-content: space-around;
}
h6{
  text-align: left;
}
.resetContainer{
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pageContainer {
  gap: 0;
}
/* give each open‐panel a little breathing room */
.pageContainer > .card,
.pageContainer > .primaryButton {
  margin-top: 30px;
  margin-bottom: 30px;
}
.resetPasswordWrapper{
  width: 100%;
  text-align: left;
  margin-top: 30px;
  margin-bottom: 15px;
}
.resetPasswordLink{
  color: var(--color-secondary-button);
  text-decoration: none;
  font-size: 27px;
}
.resetPasswordLink:hover{
  text-decoration: underline;
}

.theme-options {
  display: flex;
  gap: 8px;
}

.theme-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: var(--color-background);
  border: 2px solid var(--color-card-border);
  border-radius: 12px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
}

.theme-button:hover {
  background: var(--color-primary-button);
  border-color: var(--color-primary-button-border);
}

.theme-button.active {
  background: var(--color-primary-button);
  border-color: var(--color-primary-button-border);
}

.theme-button svg {
  font-size: 20px;
}

.theme-button span {
  font-size: 0.75rem;
  font-family: 'Oxanium', 'Inter', sans-serif;
}

/* Free User Overlay Styles */
.free-user-section {
  position: relative;
}

.feature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.feature-overlay:hover {
  background: rgba(0, 0, 0, 0.8);
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  text-align: center;
}

.overlay-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.overlay-text {
  font-size: 0.875rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Dark mode adjustments for overlays */
[data-theme="dark"] .feature-overlay {
  background: rgba(0, 0, 0, 0.8);
}

[data-theme="dark"] .feature-overlay:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* Mobile responsive adjustments for overlays */
@media (max-width: 768px) {
  .overlay-icon {
    font-size: 1.5rem;
  }
  
  .overlay-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .overlay-icon {
    font-size: 1.25rem;
  }
  
  .overlay-text {
    font-size: 0.75rem;
  }
}

</style>